import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import SortingVisualizer from '../components/SortingVisualizer';
import Controls from '../components/Controls';
import AlgorithmInfo from '../components/AlgorithmInfo';
import { generateRandomArray } from '../utils/arrayUtils';

const SortingContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
`;

const Header = styled.header`
  text-align: center;
  color: white;
  margin-bottom: 30px;
`;

const Title = styled.h1`
  font-size: 3rem;
  margin: 0;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  margin: 10px 0 0 0;
  opacity: 0.9;
`;

const BackButton = styled(Link)`
  position: absolute;
  top: 20px;
  left: 20px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  padding: 10px 20px;
  text-decoration: none;
  color: white;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
  }
`;

const MainContent = styled.main`
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 30px;
  max-width: 1400px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const LeftPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

function SortingPage() {
  const [array, setArray] = useState([]);
  const [arraySize, setArraySize] = useState(20);
  const [sortingSpeed, setSortingSpeed] = useState(100);
  const [isSorting, setIsSorting] = useState(false);
  const [currentAlgorithm, setCurrentAlgorithm] = useState('bubble');
  const [comparisons, setComparisons] = useState(0);
  const [swaps, setSwaps] = useState(0);

  const generateNewArray = useCallback(() => {
    const newArray = generateRandomArray(arraySize, 5, 100);
    setArray(newArray);
    setComparisons(0);
    setSwaps(0);
  }, [arraySize]);

  useEffect(() => {
    generateNewArray();
  }, [generateNewArray]);

  const handleSort = async (algorithm) => {
    if (isSorting) return;
    
    setIsSorting(true);
    setCurrentAlgorithm(algorithm);
    setComparisons(0);
    setSwaps(0);
    
    // Clone array to avoid mutating original
    const arrayCopy = [...array];
    
    switch (algorithm) {
      case 'bubble':
        await bubbleSort(arrayCopy);
        break;
      case 'selection':
        await selectionSort(arrayCopy);
        break;
      case 'insertion':
        await insertionSort(arrayCopy);
        break;
      case 'merge':
        await mergeSort(arrayCopy);
        break;
      case 'quick':
        await quickSort(arrayCopy);
        break;
      default:
        break;
    }
    
    setIsSorting(false);
  };

  const bubbleSort = async (arr) => {
    const n = arr.length;
    let comps = 0;
    let swapCount = 0;
    
    for (let i = 0; i < n - 1; i++) {
      for (let j = 0; j < n - i - 1; j++) {
        comps++;
        setComparisons(comps);
        
        if (arr[j] > arr[j + 1]) {
          // Swap elements
          [arr[j], arr[j + 1]] = [arr[j + 1], arr[j]];
          swapCount++;
          setSwaps(swapCount);
          
          // Update array state for visualization
          setArray([...arr]);
          
          // Delay for visualization
          await new Promise(resolve => setTimeout(resolve, 1000 / sortingSpeed));
        }
      }
    }
  };

  const selectionSort = async (arr) => {
    const n = arr.length;
    let comps = 0;
    let swapCount = 0;
    
    for (let i = 0; i < n - 1; i++) {
      let minIdx = i;
      
      for (let j = i + 1; j < n; j++) {
        comps++;
        setComparisons(comps);
        
        if (arr[j] < arr[minIdx]) {
          minIdx = j;
        }
      }
      
      if (minIdx !== i) {
        [arr[i], arr[minIdx]] = [arr[minIdx], arr[i]];
        swapCount++;
        setSwaps(swapCount);
        
        setArray([...arr]);
        await new Promise(resolve => setTimeout(resolve, 1000 / sortingSpeed));
      }
    }
  };

  const insertionSort = async (arr) => {
    const n = arr.length;
    let comps = 0;
    let swapCount = 0;
    
    for (let i = 1; i < n; i++) {
      let key = arr[i];
      let j = i - 1;
      
      while (j >= 0 && arr[j] > key) {
        comps++;
        setComparisons(comps);
        
        arr[j + 1] = arr[j];
        j--;
        swapCount++;
        setSwaps(swapCount);
        
        setArray([...arr]);
        await new Promise(resolve => setTimeout(resolve, 1000 / sortingSpeed));
      }
      
      arr[j + 1] = key;
      setArray([...arr]);
    }
  };

  const mergeSort = async (arr) => {
    try {
      const merge = async (start, mid, end) => {
        let left = arr.slice(start, mid);
        let right = arr.slice(mid, end);
        let i = 0, j = 0, k = start;
        
        while (i < left.length && j < right.length) {
          setComparisons(prev => prev + 1);
          
          if (left[i] <= right[j]) {
            arr[k] = left[i];
            i++;
          } else {
            arr[k] = right[j];
            j++;
          }
          k++;
          
          // Update array state for visualization
          setArray([...arr]);
          await new Promise(resolve => setTimeout(resolve, 1000 / sortingSpeed));
        }
        
        // Copy remaining elements
        while (i < left.length) {
          arr[k] = left[i];
          i++;
          k++;
          setArray([...arr]);
          await new Promise(resolve => setTimeout(resolve, 1000 / sortingSpeed));
        }
        
        while (j < right.length) {
          arr[k] = right[j];
          j++;
          k++;
          setArray([...arr]);
          await new Promise(resolve => setTimeout(resolve, 1000 / sortingSpeed));
        }
      };
      
      const sort = async (start, end) => {
        if (end - start <= 1) return;
        
        const mid = Math.floor((start + end) / 2);
        
        // Sort left half
        await sort(start, mid);
        // Sort right half
        await sort(mid, end);
        // Merge the two halves
        await merge(start, mid, end);
      };
      
      await sort(0, arr.length);
    } catch (error) {
      console.error('Merge sort error:', error);
      setIsSorting(false);
    }
  };

  const quickSort = async (arr) => {
    const partition = async (low, high) => {
      const pivot = arr[high];
      let i = low - 1;
      
      for (let j = low; j < high; j++) {
        setComparisons(prev => prev + 1);
        
        if (arr[j] < pivot) {
          i++;
          if (i !== j) {
            [arr[i], arr[j]] = [arr[j], arr[i]];
            setSwaps(prev => prev + 1);
            setArray([...arr]);
            await new Promise(resolve => setTimeout(resolve, 1000 / sortingSpeed));
          }
        }
      }
      
      if (i + 1 !== high) {
        [arr[i + 1], arr[high]] = [arr[high], arr[i + 1]];
        setSwaps(prev => prev + 1);
        setArray([...arr]);
        await new Promise(resolve => setTimeout(resolve, 1000 / sortingSpeed));
      }
      
      return i + 1;
    };
    
    const sort = async (low, high) => {
      if (low < high) {
        const pi = await partition(low, high);
        await sort(low, pi - 1);
        await sort(pi + 1, high);
      }
    };
    
    await sort(0, arr.length - 1);
  };

  return (
    <SortingContainer>
      <BackButton to="/">← Back to Home</BackButton>
      
      <Header>
        <Title>Sorting Algorithm Visualizer</Title>
        <Subtitle>Learn and visualize different sorting algorithms with interactive animations</Subtitle>
      </Header>
      
      <MainContent>
        <LeftPanel>
          <SortingVisualizer 
            array={array} 
            isSorting={isSorting}
            currentAlgorithm={currentAlgorithm}
          />
          <Controls 
            arraySize={arraySize}
            setArraySize={setArraySize}
            sortingSpeed={sortingSpeed}
            setSortingSpeed={setSortingSpeed}
            onGenerateArray={generateNewArray}
            onSort={handleSort}
            isSorting={isSorting}
            currentAlgorithm={currentAlgorithm}
            comparisons={comparisons}
            swaps={swaps}
          />
        </LeftPanel>
        
        <AlgorithmInfo 
          currentAlgorithm={currentAlgorithm}
          comparisons={comparisons}
          swaps={swaps}
        />
      </MainContent>
    </SortingContainer>
  );
}

export default SortingPage;
